/**
 * Listagem densa de agendamentos (HNS-1594).
 *
 * Terceira opção proposta pelo Claude Design: para a operação, que acompanha carteira e
 * precisa de volume, os cards mostram pouco por tela. Aqui cabem ~12 itens, mas as
 * decisões dos cards continuam valendo — barra de status na esquerda, encerrados
 * esmaecidos e UMA ação em destaque por linha, com o resto no menu.
 *
 * Não é a tabela antiga: aquela mantinha todos os botões visíveis em cada linha.
 *
 * Como os cards, é só apresentação: as ações vêm por callback de Agendamentos.jsx, para
 * as três visões não divergirem.
 */
import React from "react";
import { Dropdown } from "react-bootstrap";
import {
    FaRegEdit, FaEye, FaFile, FaRegCalendarAlt, FaVideo, FaBoxOpen, FaClipboardList, FaChevronDown,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { situacoesAgendamento, agendamentoEncerrado } from "../../common/utils";
import { semData } from "../Atendimentos/FluxoIntegrado/tokens";
import { idiomaAtual } from "../../i18n";

const CORES_BARRA = {
    liberado: "rgb(94,116,251)",
    agendado: "rgb(94,116,251)",
    solicitado: "rgb(255,193,7)",
    arquivo_pendente: "rgb(255,193,7)",
    em_analise: "#c3cdd9",
    finalizado: "#e8edf3",
    cancelado: "#e8edf3",
};

// "05/10 14:30" — o ano sai, porque na densa o que importa é a ordem relativa. Dia e mês
// seguem a ordem do idioma ativo (em en-US vira "10/05").
const dataCurta = (valor, comHora = true) => {
    if (semData(valor)) return null;
    const d = new Date(valor);
    if (Number.isNaN(d.getTime())) return null;
    const p = (n) => String(n).padStart(2, "0");
    const dia = new Intl.DateTimeFormat(idiomaAtual(), { day: "2-digit", month: "2-digit" }).format(d);
    return comHora ? `${dia} ${p(d.getHours())}:${p(d.getMinutes())}` : dia;
};

// A coluna "Envio Kit" da lista antiga trazia Sim/Não com previsão e realizado; aqui
// cabe uma frase curta, com o detalhe completo no title.
const resumoKit = (item, t) => {
    if (!item.atendimento_kit_id) return { texto: "—", titulo: t("agendamentos:dense.noKit") };
    const kit = item.dados_atendimento_kit || {};
    if (kit.status === "cancelado") return { texto: t("agendamentos:kit.cancelled"), titulo: t("agendamentos:dense.kitCancelledTitle") };

    const partes = [
        kit.data_envio ? t("agendamentos:dense.forecast", { data: dataCurta(kit.data_envio, false) }) : null,
        kit.data_envio_realizado ? t("agendamentos:dense.sentOn", { data: dataCurta(kit.data_envio_realizado, false) }) : null,
    ].filter(Boolean);

    if (kit.data_envio_realizado) {
        return { texto: t("agendamentos:dense.sent", { data: dataCurta(kit.data_envio_realizado, false) }), titulo: partes.join(" · ") };
    }
    return {
        texto: kit.data_envio ? t("agendamentos:dense.expected") : t("common:label.yes"),
        titulo: partes.join(" · ") || t("agendamentos:dense.kitShipping"),
    };
};

const LinhasDensasAgendamentos = ({
    itens = [],
    dadosUsuario = {},
    podeEditar = () => false,
    podeInformarData = () => false,
    ehAlteracaoDeData = () => false,
    mostrarFormularioDinamico = () => false,
    tituloFormularioDinamico,
    podeAnalisarArquivos = () => false,
    onConfirmarRealizacao,
    onInformarData,
    onFormularioDinamico,
    onArquivos,
    onConfirmarEnvioKit,
}) => {
    const { t } = useTranslation(["agendamentos", "common", "enums"]);
    const tituloFormulario = (item) => tituloFormularioDinamico?.(item) || t("agendamentos:cards.serviceForm");
    const perfil = dadosUsuario["tipo_usuario"];
    const ehPrestador = perfil === "prestador";
    const ehBeneficiario = perfil === "beneficiario";
    const mostrarPrestador = !ehPrestador;
    // O kit não é de todo programa; a coluna só entra quando algum agendamento tem kit,
    // como na lista antiga (que dependia de recebe_kit_paciente).
    const mostrarKit = itens.some((i) => i.atendimento_kit_id);

    if (itens.length === 0) {
        return (
            <div className="ag-densa">
                <div style={{ padding: "24px 18px", fontSize: "13px", color: "#6b7a8d" }}>
                    {t("agendamentos:cards.empty")}
                </div>
            </div>
        );
    }

    const colunas = [
        "68px",                                     // id
        "104px",                                    // data do agendamento
        "minmax(0, 1.1fr)",                         // paciente
        "minmax(0, 1fr)",                           // serviço
        mostrarPrestador ? "minmax(0, .8fr)" : null,
        "88px",                                     // data de solicitação
        mostrarKit ? "104px" : null,
        "108px",                                    // situação
        //Largura FIXA de proposito. Cada linha e um grid proprio, entao uma ultima coluna
        //`auto` mede o conteudo daquela linha: a linha finalizada, com so "Ver atendimento",
        //ficava mais estreita que as demais e as colunas de texto absorviam a sobra de um
        //jeito diferente -- cabecalho e linhas desalinhavam. Fixando, todas as linhas
        //resolvem o mesmo template. As acoes sao alinhadas a direita, entao onde ha menos
        //botoes sobra espaco vazio a esquerda, sem deslocar nada.
        ehPrestador ? "460px" : "300px",
    ].filter(Boolean).join(" ");

    const estiloGrade = { "--ag-densa-cols": colunas };

    return (
        <div className="ag-densa">
            <div className="ag-densa__grade ag-densa__cabecalho" style={estiloGrade}>
                <span>{t("agendamentos:table.id")}</span>
                <span>{t("agendamentos:dense.colDate")}</span>
                <span>{t("agendamentos:table.patient")}</span>
                <span>{t("agendamentos:dense.colService")}</span>
                {mostrarPrestador && <span>{t("agendamentos:table.provider")}</span>}
                <span>{t("agendamentos:dense.colRequest")}</span>
                {mostrarKit && <span>{t("agendamentos:table.kitShipping")}</span>}
                <span>{t("agendamentos:table.status")}</span>
                <span />
            </div>

            {itens.map((item) => {
                const encerrado = agendamentoEncerrado(item.situacao);
                const data = dataCurta(item.data_agendamento);
                const kitPendente = item.atendimento_kit_id && item.dados_atendimento_kit?.status === "agendado";
                const sala = item.dados_meeting?.whereby_sala_url;
                const podeEntrarNaSala = !encerrado && (ehPrestador || ehBeneficiario)
                    && item.dados_meeting?.whereby_sala_id && sala;
                const emAberto = !["finalizado", "cancelado", "arquivo_pendente"].includes(item.situacao);
                const informarData = ehPrestador && emAberto && podeInformarData(item);
                const confirmar = ehPrestador && emAberto;
                const analisar = item.situacao === "arquivo_pendente" && podeAnalisarArquivos(item);

                // Qual ação recebe o destaque (azul-marinho). As outras continuam VISÍVEIS
                // como botão neutro — nunca no menu: confirmar a realização é o trabalho
                // do profissional, esconder isso num submenu é esconder a tarefa dele.
                const jaPassou = data ? new Date(item.data_agendamento) <= new Date() : false;
                //Uma acao em destaque por linha. Confirmar a realizacao e o que fecha o
                //ciclo do agendamento, entao vem na frente; alterar data e manutencao e
                //fica neutra. A excecao e o agendamento sem data ("a combinar"): ali
                //informar a data e o que destrava o resto.
                const faltaInformarData = informarData && !ehAlteracaoDeData?.(item);
                const principal = faltaInformarData ? "data"
                    : confirmar ? "confirmar"
                    : podeEntrarNaSala ? "sala"
                    : analisar ? "arquivos"
                    : informarData ? "data"
                    : item.atendimento_uuid ? "atendimento"
                    : null;

                const classeAcao = (chave) => chave === principal ? "ag-densa__acao" : "ag-densa__acao--neutra";

                // no menu ficam só as ações acessórias
                const temMenu = [
                    emAberto && ehPrestador && mostrarFormularioDinamico(item),
                    kitPendente,
                    podeEditar(item),
                    principal !== "atendimento" && !!item.atendimento_uuid,
                ].some(Boolean);

                const kit = mostrarKit ? resumoKit(item, t) : null;
                const solicitacao = dataCurta(item.data_solicitacao, false);

                const classesLinha = ["ag-densa__grade", "ag-densa__linha"];
                if (encerrado) classesLinha.push("ag-densa__linha--encerrada");
                if (item.situacao === "cancelado") classesLinha.push("ag-densa__linha--cancelada");

                return (
                    <div
                        key={item.id}
                        className={classesLinha.join(" ")}
                        style={{ ...estiloGrade, borderLeftColor: CORES_BARRA[item.situacao] || "#e8edf3" }}
                    >
                        <span className="ag-densa__id" data-rotulo={t("agendamentos:table.id")}>{item.id}</span>

                        {data
                            ? <span className="ag-densa__data" data-rotulo={t("agendamentos:dense.colDate")}>{data}</span>
                            : <span className="ag-densa__data--combinar" data-rotulo={t("agendamentos:dense.colDate")}>{t("agendamentos:dense.toBeArranged")}</span>}

                        <span className="ag-densa__paciente ag-densa__quebra" data-rotulo={t("agendamentos:table.patient")}>
                            {item.dados_beneficiario?.nome || item.dados_mentorado?.nome || "—"}
                        </span>

                        <span className="ag-densa__servico ag-densa__quebra" data-rotulo={t("agendamentos:dense.colService")}>
                            {item.dados_servico_procedimento?.nome || "—"}
                        </span>

                        {mostrarPrestador && (
                            item.dados_prestador?.nome
                                ? <span className="ag-densa__prestador ag-densa__quebra" data-rotulo={t("agendamentos:table.provider")}>{item.dados_prestador.nome}</span>
                                : <span className="ag-densa__prestador--alerta" data-rotulo={t("agendamentos:table.provider")}>{t("agendamentos:dense.notAssigned")}</span>
                        )}

                        <span className="ag-densa__prestador" data-rotulo={t("agendamentos:dense.colRequest")}>
                            {solicitacao || "—"}
                        </span>

                        {mostrarKit && (
                            <span className="ag-densa__prestador ag-densa__quebra" data-rotulo={t("agendamentos:table.kitShipping")} title={kit.titulo}>
                                {kit.texto}
                            </span>
                        )}

                        <span data-rotulo={t("agendamentos:table.status")}>
                            <span className={`badge-agendamento ${encerrado ? "badge-ag-soft" : "badge-ag-pill"} badge-status-${item.situacao}`}>
                                {t(`enums:situacaoAgendamento.${item.situacao}`, { defaultValue: situacoesAgendamento[item.situacao] || item.situacao })}
                            </span>
                        </span>

                        <span className="ag-densa__acoes">
                            {informarData && (
                                <button type="button" className={classeAcao("data")} onClick={() => onInformarData(item)}>
                                    <FaRegCalendarAlt className="me-1" /> {ehAlteracaoDeData?.(item) ? t("agendamentos:cards.changeDate") : t("agendamentos:cards.informDate")}
                                </button>
                            )}
                            {podeEntrarNaSala && (
                                <a className={`${classeAcao("sala")} d-inline-flex align-items-center text-decoration-none`} href={`/meetings/view/${item.id}`}>
                                    <FaVideo className="me-1" /> {t("agendamentos:cards.joinRoom")}
                                </a>
                            )}
                            {confirmar && (
                                <button type="button" className={classeAcao("confirmar")} onClick={() => onConfirmarRealizacao(item)}>
                                    {t("agendamentos:cards.confirmCompletion")}
                                </button>
                            )}
                            {analisar && (
                                <button type="button" className={classeAcao("arquivos")} onClick={() => onArquivos(item)}>
                                    <FaFile className="me-1" /> {t("agendamentos:cards.reviewFiles")}
                                </button>
                            )}
                            {principal === "atendimento" && (
                                <a className="ag-densa__acao--neutra d-inline-flex align-items-center text-decoration-none" href={`/atendimentos/edit/${item.atendimento_uuid}`}>
                                    <FaEye className="me-1" /> {t("agendamentos:cards.viewEncounter")}
                                </a>
                            )}

                            {temMenu && (
                                <Dropdown align="end">
                                    <Dropdown.Toggle
                                        as="button"
                                        type="button"
                                        className="ag-densa__mais"
                                        id={`acoes-densa-${item.id}`}
                                        title={t("agendamentos:cards.moreActions")}
                                    >
                                        <FaChevronDown />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {emAberto && ehPrestador && mostrarFormularioDinamico(item) && (
                                            <Dropdown.Item onClick={() => onFormularioDinamico(item)}>
                                                <FaClipboardList className="me-2" /> {tituloFormulario(item)}
                                            </Dropdown.Item>
                                        )}
                                        {kitPendente && (
                                            <Dropdown.Item onClick={() => onConfirmarEnvioKit(item.atendimento_kit_id, item)}>
                                                <FaBoxOpen className="me-2" /> {t("agendamentos:cards.confirmKitShipping")}
                                            </Dropdown.Item>
                                        )}
                                        {podeEditar(item) && (
                                            <Dropdown.Item href={`/agendamentos/edit/${item.id}`}>
                                                <FaRegEdit className="me-2" /> {t("agendamentos:cards.editAppointment")}
                                            </Dropdown.Item>
                                        )}
                                        {principal !== "atendimento" && item.atendimento_uuid && (
                                            <Dropdown.Item href={`/atendimentos/edit/${item.atendimento_uuid}`}>
                                                <FaEye className="me-2" /> {t("agendamentos:cards.viewEncounter")}
                                            </Dropdown.Item>
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

export default LinhasDensasAgendamentos;
